export interface SenderIdDto {
  id: string;
  senderId: string;
  status: 'pending' | 'approved' | 'rejected' | 'active' | 'suspended';
  description?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SenderIdAllocationDto {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  status: 'active' | 'suspended' | 'revoked';
  allocatedBy?: string | null;
  allocatedAt: string;
  createdAt: string;
  updatedAt: string;
}

import { createAuthRequestInit } from '../utils/auth';
import { API_BASE, getAbsoluteApiUrl } from '../config/api';

export async function getSenderIds(params?: { status?: string; q?: string }): Promise<SenderIdDto[]> {
  const url = new URL(getAbsoluteApiUrl('/sender-ids'));
  if (params?.status) url.searchParams.set('status', params.status);
  if (params?.q) url.searchParams.set('q', params.q);
  const res = await fetch(url.toString(), createAuthRequestInit());
  if (!res.ok) throw new Error('Failed to fetch sender IDs');
  const json = await res.json();
  return json.data.senderIds as SenderIdDto[];
}

export async function getSenderId(id: string): Promise<SenderIdDto> {
  const res = await fetch(`${API_BASE}/sender-ids/${encodeURIComponent(id)}`, createAuthRequestInit());
  if (!res.ok) throw new Error('Failed to fetch sender ID');
  const json = await res.json();
  return json.data.senderId as SenderIdDto;
}

export async function createSenderId(payload: { senderId: string; description?: string }): Promise<SenderIdDto> {
  const res = await fetch(
    `${API_BASE}/sender-ids`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || 'Failed to create sender ID');
  }
  const json = await res.json();
  return json.data.senderId as SenderIdDto;
}

export async function updateSenderId(id: string, payload: { status?: string; description?: string | null }): Promise<SenderIdDto> {
  const res = await fetch(
    `${API_BASE}/sender-ids/${encodeURIComponent(id)}`,
    createAuthRequestInit(
      {
        method: 'PUT',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || 'Failed to update sender ID');
  }
  const json = await res.json();
  return json.data.senderId as SenderIdDto;
}

export async function deleteSenderId(id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/sender-ids/${encodeURIComponent(id)}`,
    createAuthRequestInit({
      method: 'DELETE'
    })
  );
  if (!res.ok && res.status !== 204) {
    const t = await res.text();
    throw new Error(t || 'Failed to delete sender ID');
  }
}

export async function getAllocations(senderIdId: string): Promise<SenderIdAllocationDto[]> {
  const res = await fetch(`${API_BASE}/sender-ids/${encodeURIComponent(senderIdId)}/allocations`, createAuthRequestInit());
  if (!res.ok) throw new Error('Failed to fetch allocations');
  const json = await res.json();
  return json.data.allocations as SenderIdAllocationDto[];
}

export async function allocateSenderId(senderIdId: string, userId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/sender-ids/${encodeURIComponent(senderIdId)}/allocate`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify({ userId })
      },
      true
    )
  );
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || 'Failed to allocate sender ID');
  }
}

export async function revokeSenderId(senderIdId: string, userId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/sender-ids/${encodeURIComponent(senderIdId)}/allocations/${encodeURIComponent(userId)}`,
    createAuthRequestInit({
      method: 'DELETE'
    })
  );
  if (!res.ok && res.status !== 204) {
    const t = await res.text();
    throw new Error(t || 'Failed to remove allocation');
  }
}

export async function getUserSenderIds(userId: string): Promise<SenderIdDto[]> {
  const res = await fetch(`${API_BASE}/sender-ids/user/${encodeURIComponent(userId)}`, createAuthRequestInit());
  if (!res.ok) throw new Error('Failed to fetch user sender IDs');
  const json = await res.json();
  return json.data.senderIds as SenderIdDto[];
}


